"use client"

import type * as React from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { PanelLeft } from "lucide-react"
import { useAuth } from "@/components/auth-provider"
import { TeamSwitcher } from "@/components/team-switcher"
import { DashboardIcon } from "@/components/dashboard-icon"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { NavUser } from "@/components/nav-user"
import Image from "next/image"

const teams = [
  {
    name: "Zipsprout",
    logo: ({ className }: { className?: string } = {}) => (
      <Image
        src="/zipsprout-icon.png"
        alt="Zipsprout"
        width={16}
        height={16}
        className={className || "size-4"}
        style={{
          width: "auto",
          height: "100%",
          objectFit: "contain",
          mixBlendMode: "screen",
        }}
      />
    ),
    plan: "Enterprise",
  },
]

export function ClientSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const pathname = usePathname()
  const { user } = useAuth()
  const { toggleSidebar, state } = useSidebar()
  const isCollapsed = state === "collapsed"

  const dashboardActive = pathname === "/client/dashboard"

  const displayName =
    user?.name?.trim() ||
    user?.company_name?.trim() ||
    (user?.email ? user.email.split("@")[0] : "") ||
    "Account"
  const email = user?.email ?? ""

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={teams} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Platform</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={dashboardActive} tooltip="Dashboard" className="hover:text-[#2563eb]">
                <Link href="/client/dashboard">
                  <DashboardIcon />
                  <span>Dashboard</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"} onClick={toggleSidebar} className="hover:text-[#2563eb]">
              <PanelLeft />
              <span>{isCollapsed ? "Expand" : "Collapse"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser minimalMenu user={{ name: displayName, email }} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
